import {
  Autocomplete,
  AutocompleteRenderOptionState,
  Chip,
  CircularProgress,
  TextField,
  TextFieldProps,
  createFilterOptions,
} from "@mui/material";
import { AxiosResponse } from "axios";
import {
  HTMLAttributes,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useDebounce from "../../../hooks/useDebounce";
import useFetch from "../../../hooks/useFetch";

export interface ISelectType {
  id: string;
  name: string;
  value?: string;
  code?: string;
  description?: string;
  procedureCode?: number;
  procedureInternalCode?: number;
  procedureOriginCode?: number;
  calendarIdentifiers?: number[];
}

type IFormInputProps = {
  name: string;
  options?: ISelectType[];
  onValueSelected?: (data: ISelectType | null) => void;
  request?: (params?: any) => Promise<AxiosResponse<ISelectType[]>>;
  serviceType?: "List" | "Page";
  params?: any;
  SearchAsYouTypeParams?: (typedValue: string) => any;
  getOptionLabel: ((option: ISelectType) => string) | undefined;
  isOptionEqualToValue?: (option: ISelectType, value: ISelectType) => boolean;
  filterSelectedOptions?: boolean;
  isClearable?: boolean;
  disable?: boolean;
  loading?: boolean;
  renderOption?:
    | ((
        props: HTMLAttributes<HTMLLIElement>,
        option: ISelectType,
        state: AutocompleteRenderOptionState
      ) => ReactNode)
    | undefined;
} & TextFieldProps;

const filterOptions = createFilterOptions({
  matchFrom: "any",
  stringify: (option: ISelectType) => option.name + option.code,
});

const AutoComplete = ({
  name,
  options,
  onValueSelected,
  params,
  request,
  getOptionLabel,
  SearchAsYouTypeParams,
  isOptionEqualToValue = (o, v) => {
    return o.id === v.id;
  },
  disable,
  filterSelectedOptions = false,
  isClearable = true,
  loading: externalLoading = false,
  renderOption = (props, option) => {
    return (
      <li {...props} key={option.id}>
        {option.name}
      </li>
    );
  },
  ...otherProps
}: IFormInputProps) => {
  const { control } = useFormContext();
  const [realOptions, setOptions] = useState<ISelectType[]>(options || []);
  const { loading, sendRequest } = useFetch(request);
  const { t } = useTranslation();
  const [value, setValue] = useState<string>("");
  const isLoading = loading || externalLoading;

  const searchQuery: string = useDebounce(value, 500);

  const loadRequest = useCallback(
    async (parameters?: any) => {
      const { data, success } = await sendRequest(parameters);

      if (success) {
        if (searchQuery) {
          setOptions(
            data.filter((d) =>
              d.name
                .toLocaleLowerCase()
                .includes(searchQuery.toLocaleLowerCase())
            )
          );
        } else {
          setOptions(data);
        }
      }
    },
    [sendRequest, searchQuery]
  );

  //Debounce get function
  useEffect(() => {
    if (SearchAsYouTypeParams) {
      if (searchQuery !== "" && searchQuery.length > 2) {
        loadRequest(SearchAsYouTypeParams(searchQuery));
      }
    }
  }, [searchQuery, SearchAsYouTypeParams, loadRequest]);

  useEffect(() => {
    if (!SearchAsYouTypeParams) {
      loadRequest(params);
    }
  }, [loadRequest, SearchAsYouTypeParams, params]);

  return (
    <Controller
      render={({ fieldState: { error }, field }) => {
        const { onChange, value, ref } = field;
        return (
          <Autocomplete
            value={value}
            loading={isLoading}
            loadingText={t("components.autocomplete.loading")}
            disabled={disable ? true : false}
            noOptionsText={t("components.autocomplete.noOptions")}
            renderOption={renderOption}
            isOptionEqualToValue={isOptionEqualToValue}
            filterSelectedOptions={filterSelectedOptions}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  color="primary"
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            filterOptions={
              params ? filterOptions : options ? undefined : (x) => x
            }
            options={options ? options : realOptions}
            getOptionLabel={getOptionLabel}
            onChange={(event: any, newValue) => {
              if (!newValue) {
                onChange(null);
              }
              if (onValueSelected) {
                onValueSelected(newValue);
              }
              if (newValue) {
                onChange(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                {...otherProps}
                error={!!error}
                onChange={(e) => setValue(e.target.value)}
                inputRef={ref}
                InputProps={{
                  ...params.InputProps,
                  ...otherProps.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                helperText={
                  error
                    ? t(error?.message || "")
                    : SearchAsYouTypeParams
                    ? t("components.autocomplete.hint")
                    : ""
                }
                fullWidth
              />
            )}
          />
        );
      }}
      defaultValue={null}
      name={name}
      control={control}
    />
  );
};

export default memo(AutoComplete);
